import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Building2, ArrowLeft, Users, Home, Loader, Sparkles } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const SignUp = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "landlord" as "landlord" | "tenant"
  });
  const [loading, setLoading] = useState(false);
  const [lastSubmissionTime, setLastSubmissionTime] = useState(0);
  
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if this is a trial signup
  const isTrialSignup = searchParams.get('trial') === 'true';
  const trialPlan = searchParams.get('plan') || 'starter';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent rapid submissions (rate limiting)
    const now = Date.now();
    if (now - lastSubmissionTime < 2000) {
      toast({
        title: "Please wait",
        description: "Please wait a moment before trying again.",
        variant: "destructive"
      });
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Password mismatch",
        description: "Passwords don't match. Please try again.",
        variant: "destructive"
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters long.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    setLastSubmissionTime(now);

    try {
      // Set custom redirect URL to redirect to login after confirmation
      const customRedirectUrl = `${window.location.origin}/login?confirmed=true`;
      
      const { error, isExistingUser } = await signUp(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName,
        'landlord',
        isTrialSignup ? trialPlan : undefined,
        customRedirectUrl
      );
      
      if (error) {
        // Handle rate limiting error specifically
        if (error.message?.includes('rate limit') || error.message?.includes('6 seconds')) {
          toast({
            title: "Too many attempts",
            description: "Please wait a moment before trying again.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Registration failed",
            description: error.message,
            variant: "destructive"
          });
        }
      } else {
        // Both new accounts and existing unconfirmed accounts should show confirmation page
        const message = isExistingUser 
          ? "Confirmation email resent. Please check your email for a confirmation link."
          : "Account created successfully! Please check your email for a confirmation link.";
        
        toast({
          title: "Success!",
          description: message,
          variant: "default"
        });
        
        // Redirect to email confirmation page with proper parameters
        const params = new URLSearchParams();
        params.append('email', formData.email);
        if (isTrialSignup) {
          params.append('trial', 'true');
          params.append('plan', trialPlan);
        }
        navigate(`/email-sent?${params.toString()}`);
      }
    } catch (error: any) {
      toast({
        title: "Registration failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Home */}
        <Link to="/" className="inline-flex items-center text-primary hover:text-primary/80 mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <Card className="shadow-lg border border-border bg-card">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Building2 className="h-8 w-8 text-primary" />
              <span className="text-2xl font-bold text-foreground">RentFlow</span>
            </div>
            {isTrialSignup && (
              <Badge variant="secondary" className="mb-4 bg-accent/10 text-accent border-accent/20">
                <Sparkles className="h-4 w-4 mr-1" />
                14-Day Free Trial - No Credit Card Required
              </Badge>
            )}
            <CardTitle className="text-2xl font-bold text-foreground">
              {isTrialSignup ? `Start Your ${trialPlan.charAt(0).toUpperCase() + trialPlan.slice(1)} Trial` : 'Create Your Account'}
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {isTrialSignup 
                ? `Get instant access to all ${trialPlan} features for 14 days` 
                : 'Join thousands of property managers using RentFlow'
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Landlord Registration Notice */}
              <div className="space-y-3">
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Home className="h-5 w-5 text-primary" />
                    <span className="font-medium text-primary">Landlord Registration</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    This registration is for property owners and managers. 
                    Tenants will receive invitation links from their landlords to create accounts.
                  </p>
                </div>
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange("firstName", e.target.value)}
                    required
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange("lastName", e.target.value)}
                    required
                    className="h-12"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  required
                  className="h-12"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  required
                  className="h-12"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                  required
                  className="h-12"
                />
              </div>
              
              
              <div className="flex items-center space-x-2">
                <input
                  id="terms"
                  type="checkbox"
                  required
                  className="rounded border-border text-primary focus:ring-primary"
                />
                <Label htmlFor="terms" className="text-sm text-muted-foreground">
                  I agree to the{" "}
                  <a href="#" className="text-primary hover:text-primary/80">
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a href="#" className="text-primary hover:text-primary/80">
                    Privacy Policy
                  </a>
                </Label>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
              >
                {loading ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                 ) : (
                   isTrialSignup ? `Start ${trialPlan.charAt(0).toUpperCase() + trialPlan.slice(1)} Trial` : "Create Account"
                 )}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-muted-foreground">
                Already have an account?{" "}
                <Link to="/login" className="text-primary hover:text-primary/80 font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SignUp;
